import { Link, useLocation, useParams } from "react-router-dom";

/**
 * Breadcrumb item configuration
 */
interface BreadcrumbItem {
  label: string;
  href?: string;
}

/**
 * AdminBreadcrumbs - Hierarchical navigation breadcrumbs for admin panel.
 *
 * Automatically generates breadcrumbs based on current route.
 */
export function AdminBreadcrumbs() {
  const location = useLocation();
  const params = useParams();

  const breadcrumbs = generateBreadcrumbs(location.pathname, params);

  if (breadcrumbs.length <= 1) {
    return (
      <nav aria-label="Breadcrumb">
        <h1 className="text-lg font-semibold text-gray-900">
          {breadcrumbs[0]?.label || "Dashboard"}
        </h1>
      </nav>
    );
  }

  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex items-center gap-2 text-sm">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <li key={index} className="flex items-center gap-2">
              {index > 0 && (
                <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              )}

              {isLast || !crumb.href ? (
                <span
                  className={`${
                    isLast
                      ? "font-semibold text-gray-900"
                      : "text-gray-500"
                  }`}
                  aria-current={isLast ? "page" : undefined}
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.href}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Generate breadcrumb items based on current pathname
 */
function generateBreadcrumbs(
  pathname: string,
  params: Record<string, string | undefined>
): BreadcrumbItem[] {
  const segments = pathname.split("/").filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];

  // Skip if not in admin
  if (segments[0] !== "admin") {
    return [{ label: "Admin", href: "/admin" }];
  }

  // Always start with Dashboard
  breadcrumbs.push({ label: "Dashboard", href: "/admin" });

  // Build breadcrumbs based on route structure
  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];
    const href = "/" + segments.slice(0, i + 1).join("/");

    // Map segment to readable label
    switch (segment) {
      case "users":
        breadcrumbs.push({ label: "Users", href });
        break;

      case "analytics":
        breadcrumbs.push({ label: "Analytics", href });
        break;

      case "progress":
        if (segments[i - 1] === "analytics") {
          breadcrumbs.push({ label: "Progress", href });
        }
        break;

      case "conversions":
        if (segments[i - 1] === "analytics") {
          breadcrumbs.push({ label: "Conversions", href });
        }
        break;

      case "emails":
        breadcrumbs.push({ label: "Emails", href });
        break;

      case "settings":
        breadcrumbs.push({ label: "Settings", href });
        break;

      case "children":
        breadcrumbs.push({ label: "Children", href });
        break;

      default:
        // Handle dynamic segments (IDs)
        if (segment && segment === params.profileId) {
          breadcrumbs.push({
            label: `User ${segment.substring(0, 8)}...`,
            href,
          });
        } else if (segment && segment === params.childId) {
          breadcrumbs.push({
            label: `Child ${segment.substring(0, 8)}...`,
            href,
          });
        }
        break;
    }
  }

  return breadcrumbs;
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default AdminBreadcrumbs;
